import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Box,
  Button,
  ButtonProps,
  Collapse,
  Container,
  Divider,
  IconButton,
  Stack,
  SxProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

import { TP_COLORS } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import {
  getTrials,
  getTrialsState,
  resetTrials,
  toggleBookTrialModal,
} from '../../../store/V2/TrialsSlice';

import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../components/BreadCrumb/Breadcrumbs';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import TrialClassListItem from './components/TrialClassListItem';

const boxStyles: SxProps = {
  background: `url('${process.env.PUBLIC_URL}/images/trial/bg-banner.png') top right/contain no-repeat, linear-gradient(107.83deg, #003768 7.88%, #006C9C 41.91%, #39BFB9 82.16%)`,
  paddingBottom: '42px',
  marginBottom: '50px',
};

const buttonProps: ButtonProps = {
  disableElevation: true,
  color: 'inherit',
  variant: 'contained',
  sx: {
    height: '45px',
    marginTop: '10px',
    fontWeight: '600',
    backgroundColor: TP_COLORS.listItemGray.light,
  },
};

const constructBreadcrumbOptions = () => {
  const options: Array<BreadcrumbOption> = [
    {
      type: 'link',
      title: 'Home',
      link: `${process.env.PUBLIC_URL}`,
    },
    {
      type: 'text',
      title: 'Trial Classes',
      highlight: true,
    },
  ];

  return options;
};

const TrialClasses: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const breadcrumbOptions = constructBreadcrumbOptions();

  // Store
  const { trials, status } = useAppSelector(getTrialsState);

  // State
  const [isUpcomingCollapseOpen, setUpcomingCollapseOpen] = useState(true);
  const [isCompletedCollapseOpen, setCompletedCollapseOpen] = useState(true);

  useEffect(() => {
    dispatch(getTrials());

    return () => {
      dispatch(resetTrials());
    };
  }, []);

  const toggleUpcomingCollapse = () => {
    setUpcomingCollapseOpen(!isUpcomingCollapseOpen);
  };

  const toggleCompletedCollapse = () => {
    setCompletedCollapseOpen(!isCompletedCollapseOpen);
  };

  return (
    <>
      <Box sx={boxStyles}>
        <Container maxWidth="lg">
          <div style={{ padding: `30px 0 ${isXsScreen ? '15px' : '0'} 0` }}>
            <Breadcrumbs
              options={breadcrumbOptions}
              showDropDown={isXsScreen}
            />
          </div>
          <div style={{ textAlign: 'center' }}>
            <Typography color="#F8FAFC" fontWeight="700" fontSize="30px">
              Your Free Trial Classes
            </Typography>

            <Button
              {...buttonProps}
              onClick={() => dispatch(toggleBookTrialModal())}
            >
              Book your next FREE Class
            </Button>
          </div>
        </Container>
      </Box>
      <Container maxWidth="md">
        {/* Upcoming Trial Classes */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" flexGrow={1}>
            <IconButton
              style={{ marginRight: '5px' }}
              onClick={toggleUpcomingCollapse}
            >
              {isUpcomingCollapseOpen ? (
                <ExpandLessIcon style={{ color: TP_COLORS.black }} />
              ) : (
                <ExpandMoreIcon style={{ color: TP_COLORS.black }} />
              )}
            </IconButton>
            <Typography fontSize="25px" lineHeight="30px" fontWeight={700}>
              Upcoming Trial Classes
            </Typography>
          </Stack>
        </Stack>
        <Collapse in={isUpcomingCollapseOpen}>
          <div
            style={{
              padding: `0 ${isXsScreen ? '5px' : '10px'}`,
              marginTop: '40px',
            }}
          >
            <Stack>
              {status === 'loading' && <BasicLoader />}
              {trials.upcomingTrials.map((trial, index) => (
                <TrialClassListItem key={index} trialData={trial} />
              ))}
              {trials.upcomingTrials.length === 0 && status === 'idle' ? (
                <Typography variant="h6" textAlign="center">
                  No upcoming classes found
                </Typography>
              ) : null}
            </Stack>
          </div>
        </Collapse>

        <Divider flexItem sx={{ borderStyle: 'dashed', margin: '40px 0px' }} />

        {/* Completed Trial Classes */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" flexGrow={1}>
            <IconButton
              style={{ marginRight: '5px' }}
              onClick={toggleCompletedCollapse}
            >
              {isCompletedCollapseOpen ? (
                <ExpandLessIcon style={{ color: TP_COLORS.black }} />
              ) : (
                <ExpandMoreIcon style={{ color: TP_COLORS.black }} />
              )}
            </IconButton>
            <Typography fontSize="25px" lineHeight="30px" fontWeight={700}>
              Completed Trial Classes
            </Typography>
          </Stack>
        </Stack>
        <Collapse in={isCompletedCollapseOpen}>
          <div
            style={{
              padding: `0 ${isXsScreen ? '5px' : '10px'}`,
              marginTop: '40px',
            }}
          >
            <Stack>
              {status === 'loading' && <BasicLoader />}
              {trials.completedTrials.map((trial, index) => (
                <TrialClassListItem key={index} trialData={trial} isCompleted />
              ))}
              {trials.completedTrials.length === 0 && status === 'idle' ? (
                <Typography variant="h6" textAlign="center">
                  No completed classes found
                </Typography>
              ) : null}
            </Stack>
          </div>
        </Collapse>
      </Container>
    </>
  );
};

export default TrialClasses;
